import { useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { useAppState } from "Context/StateContext";
import { Warehouse } from "Models/warehouse";
import { MapCell } from "Models/warehouse.mapcell";
import WarehouseMapCellService from "Services/warehouse.map.cell.service";
import WarehouseService from "Services/warehouse.service";
import { Status } from "Util/enums";

export const useActions = () => {
  const { currentUser } = useAppState();

  const [branchIds, setBranchIds] = useState<number[]>([]);
  const [warehouseList, setWarehouseList] = useState<Warehouse[]>([]);
  const [mapId, setMapId] = useState<number>();
  const [mapCells, setMapCells] = useState<MapCell[]>();

  const defaultAttribute = ["id", "name"];

  const { isFetching: isWarehouseListFetching } = useQuery(
    ["warehouse.list", currentUser, branchIds],
    () =>
      WarehouseService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
        include: JSON.stringify([
          {
            association: "branches",
            attributes: [...defaultAttribute],
            where: { id: branchIds },
          },
          {
            association: "sections",
            attributes: [...defaultAttribute, "allowsItemLoose", "allowsStorageBox", "isActive"],
            include: [
              {
                association: "maps",
                attributes: [...defaultAttribute, "description", "isActive"],
              },
              {
                association: "storageBoxTypes",
                attributes: defaultAttribute,
              },
            ],
          },
        ]),
      }),
    {
      enabled: !warehouseList?.length || !!currentUser,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        setWarehouseList(response.data.filter((w) => w.sections?.some((s) => s.allowsItemLoose)));
      },
    }
  );

  const { isFetching: isMapCellsFetching } = useQuery(
    ["mapcells", mapId],
    () =>
      WarehouseMapCellService.getAll({
        mapId: Number(mapId),
        include: JSON.stringify([
          {
            association: "itemsJobs",
            attributes: ["id", "description"],
          },
          {
            association: "storageBoxes",
            attributes: ["id", "number"],
          },
          {
            association: "map",
            include: [{ association: "section" }, { association: "warehouse" }],
          },
        ]),
        sortBy: JSON.stringify([
          ["indexRow", "asc"],
          ["indexColumn", "asc"],
          ["indexLevel", "asc"],
        ]),
      }),
    {
      enabled: !!mapId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data.length) {
          setMapCells(response.data.filter((c) => c.isActive));
        }
      },
    }
  );

  return {
    warehouseList,
    mapCells,
    setMapId,
    isWarehouseListFetching,
    isMapCellsFetching,
    setBranchIds,
  };
};
